import useOfferDetailViewerKey from 'tripPlanner/hooks/useOfferDetailViewerKey'
import { useEffect, useState } from 'react'
import type { getOfferDetail } from 'tripPlanner/api'
import {
  useOfferDetailsQuery,
  type UseOfferDetailsOptions,
} from './useOfferDetails'

interface LastSuccessfulOfferDetail {
  identity: string
  offerDetail: Awaited<ReturnType<typeof getOfferDetail>>
}

export default function useRetainedOfferDetails(options: UseOfferDetailsOptions) {
  const {
    offerId,
    region,
    offerType,
    accommodationPropertyId,
    orderId,
  } = options
  const viewerKey = useOfferDetailViewerKey()
  const offerIdentity = JSON.stringify([
    viewerKey,
    offerId,
    region,
    offerType,
    accommodationPropertyId,
    orderId,
  ])
  const [lastSuccessfulOfferDetail, setLastSuccessfulOfferDetail] =
    useState<LastSuccessfulOfferDetail | undefined>(undefined)
  const queryResult = useOfferDetailsQuery({
    ...options,
    keepPreviousData: true,
  })

  const canRetainLastSuccessfulOffer =
    lastSuccessfulOfferDetail?.identity === offerIdentity
  const previousQueryDataBelongsToOffer =
    !queryResult.isPreviousData || canRetainLastSuccessfulOffer
  const currentOfferDetail = previousQueryDataBelongsToOffer
    ? queryResult.data
    : undefined

  useEffect(() => {
    if (currentOfferDetail && !queryResult.isPreviousData) {
      setLastSuccessfulOfferDetail({
        identity: offerIdentity,
        offerDetail: currentOfferDetail,
      })
    }
  }, [currentOfferDetail, offerIdentity, queryResult.isPreviousData])

  const retainedOfferDetail = canRetainLastSuccessfulOffer
    ? lastSuccessfulOfferDetail?.offerDetail
    : undefined
  const offerDetail = currentOfferDetail ?? retainedOfferDetail
  const isUsingPreviousData = !!offerDetail && (
    queryResult.isPreviousData || !currentOfferDetail
  )
  const isLoading = !offerDetail &&
    queryResult.fetchStatus !== 'idle' &&
    !queryResult.isError

  return {
    offerDetail,
    isLoading,
    isUsingPreviousData,
    isError: queryResult.isError,
    refetch: queryResult.refetch,
  }
}
